import { cn } from "@app/lib/utils";
import { trans } from "@mongez/localization";
import { useMemo, useState } from "react";

export type ReadMoreTextProps = {
  children: string;
  /** Collapsed view shows at most this many characters (then optional word boundary). */
  maxChars?: number;
  /** When true, prefers breaking at the last space before `maxChars`. */
  breakAtWord?: boolean;
  /** Appends an ellipsis when collapsed and text is truncated. */
  showEllipsis?: boolean;
  className?: string;
  textClassName?: string;
  buttonClassName?: string;
  readMoreLabel?: string;
  readLessLabel?: string;
};

function truncateText(text: string, maxChars: number, breakAtWord: boolean) {
  const trimmed = text.trim();
  if (trimmed.length <= maxChars) {
    return trimmed;
  }
  if (!breakAtWord) {
    return trimmed.slice(0, maxChars).trimEnd();
  }
  const slice = trimmed.slice(0, maxChars);
  const lastSpace = slice.lastIndexOf(" ");
  if (lastSpace > maxChars * 0.5) {
    return slice.slice(0, lastSpace).trimEnd();
  }
  return slice.trimEnd();
}

export function ReadMoreText({
  children,
  maxChars = 300,
  breakAtWord = true,
  showEllipsis = true,
  className,
  textClassName,
  buttonClassName,
  readMoreLabel,
  readLessLabel,
}: ReadMoreTextProps) {
  const [expanded, setExpanded] = useState(false);

  const fullText = useMemo(() => children.trim(), [children]);

  const collapsedText = useMemo(
    () => truncateText(children, maxChars, breakAtWord),
    [children, maxChars, breakAtWord],
  );

  const needsToggle = fullText.length > collapsedText.length;
  const displayText = expanded ? fullText : collapsedText;

  const moreLabel = readMoreLabel ?? trans("common.readMore");
  const lessLabel = readLessLabel ?? trans("common.readLess");

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <p className={textClassName}>
        {displayText}
        {showEllipsis && needsToggle && !expanded ? (
          <span aria-hidden="true">{"\u2026"}</span>
        ) : null}
      </p>
      {needsToggle ? (
        <button
          type="button"
          className={cn(
            "self-start border-none bg-transparent p-0 text-sm font-semibold underline underline-offset-2 transition-colors hover:text-steel focus:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2",
            buttonClassName,
          )}
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      ) : null}
    </div>
  );
}
